import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CalendarClock, Banknote, Loader2, ShieldCheck, Smartphone, FileSignature } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { API, formatApiErrorDetail, useAuth } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

export default function BookingDialog({ open, onOpenChange, educator, defaultSubject }) {
  const { t } = useTranslation();
  const fmt = useFmtXof();
  const { user } = useAuth();
  const navigate = useNavigate();
  const rate = Number(educator?.profile?.hourly_rate || 0);
  const [hours, setHours] = useState(16);
  const [subject, setSubject] = useState(defaultSubject || educator?.profile?.subjects?.[0] || "");
  const [scheduledAt, setScheduledAt] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setSubject(defaultSubject || educator?.profile?.subjects?.[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, educator?.id, defaultSubject]);

  const amounts = useMemo(() => {
    const gross = Math.round(rate * (Number(hours) || 0));
    const fee = Math.round(gross * 0.15);
    return { gross, fee, net: gross - fee };
  }, [rate, hours]);

  const reset = () => {
    setHours(16);
    setSubject(defaultSubject || educator?.profile?.subjects?.[0] || "");
    setScheduledAt(""); setPhone(""); setNotes("");
  };

  const cgpsAccepted = !!(user?.legal_acceptance?.accepted_at);

  const onConfirm = async () => {
    if (!educator?.id) return;
    if (!subject || !hours) { toast.error(t("booking.missingSubject")); return; }
    setSubmitting(true);
    try {
      const { data: booking } = await axios.post(`${API}/bookings`, {
        educator_id: educator.id, subject, hours: Number(hours),
        scheduled_at: scheduledAt || null, notes,
      });
      const { data: checkout } = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.id, phone: phone || null, payment_method: "any",
      });
      if (checkout?.payment_url) {
        toast.success(t("booking.redirecting"));
        window.location.href = checkout.payment_url;
      } else {
        toast.error(t("booking.noPaymentUrl"));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A2518]">{t("booking.title", { name: educator?.name })}</DialogTitle>
          <DialogDescription>{t("booking.subtitle")}</DialogDescription>
        </DialogHeader>

        {!cgpsAccepted && (
          <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm" data-testid="booking-cgps-warning">
            <div className="flex items-start gap-2 text-yellow-800">
              <FileSignature className="h-5 w-5 mt-0.5 shrink-0" />
              <div>
                <strong>{t("booking.cgpsStrong")}</strong>
                <p className="mt-1 text-yellow-700">{t("booking.cgpsText")}</p>
                <Button type="button" onClick={() => { onOpenChange(false); navigate("/legal/parents"); }} className="mt-3 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="booking-cgps-link">
                  {t("booking.cgpsLink")}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-[#0A2518]">{t("booking.subjectLabel")}</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5 h-11" data-testid="booking-subject-input" />
          </div>
          <div>
            <Label className="text-[#0A2518] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> {t("booking.hoursLabel")}</Label>
            <Input type="number" min={16} max={80} step={4} value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1.5 h-11" data-testid="booking-hours-input" />
            <p className="mt-1 text-xs text-[#4B5563]">{t("booking.hoursHint")}</p>
          </div>
          <div>
            <Label className="text-[#0A2518]">{t("booking.dateLabel")}</Label>
            <Input type="date" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="mt-1.5 h-11" data-testid="booking-date-input" />
          </div>
          <div className="col-span-2">
            <Label className="text-[#0A2518] flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" /> {t("booking.phoneLabel")}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+229 …" className="mt-1.5 h-11" data-testid="booking-phone-input" />
            <p className="mt-1 text-xs text-[#4B5563]">{t("booking.phoneHint")}</p>
          </div>
          <div className="col-span-2">
            <Label className="text-[#0A2518]">{t("booking.notesLabel")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 min-h-[70px]" placeholder={t("booking.notesPh")} data-testid="booking-notes-input" />
          </div>
        </div>

        <div className="mt-2 rounded-xl bg-[#F0FDF4] border border-[#16A34A]/20 p-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[#4B5563] flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5 text-[#16A34A]" /> {t("booking.rate")}</span>
            <span className="font-medium text-[#0A2518]">{fmt(rate)}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[#4B5563]">{t("booking.subtotal", { hours: hours || 0 })}</span>
            <span className="font-medium text-[#0A2518]">{fmt(amounts.gross)}</span>
          </div>
          <div className="flex items-center justify-between mt-1 text-xs">
            <span className="text-[#4B5563]">{t("booking.commission")}</span>
            <span className="text-[#4B5563]">{fmt(amounts.fee)}</span>
          </div>
          <div className="flex items-center justify-between mt-1 text-xs">
            <span className="text-[#4B5563]">{t("booking.educatorShare")}</span>
            <span className="text-[#4B5563]">{fmt(amounts.net)}</span>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#16A34A]/20 font-display">
            <span className="text-[#0A2518] font-semibold">{t("booking.total")}</span>
            <span className="text-[#15803D] text-lg font-bold" data-testid="booking-total">{fmt(amounts.gross)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-[#4B5563]">
          <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" />
          {t("booking.secureNote")}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>{t("common.cancel")}</Button>
          <Button onClick={onConfirm} disabled={submitting || !subject || !hours} className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-full" data-testid="booking-pay-btn">
            {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("booking.preparing")}</>) : t("booking.payBtn", { amount: fmt(amounts.gross) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
